import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";

import { supabase } from "../providers/supabase/supabase";

type EntityType = { id: number; name: string; unit: string };
type Crop = { id: number; name: string };
type Bracket = {
  id: number;
  entity_type_id: number;
  crop_id: number;
  min_value: number;
  max_value: number | null;
  rate_per_unit: number;
};

type ComboKey = `${number}-${number}`;
const comboKey = (etId: number, cropId: number): ComboKey =>
  `${etId}-${cropId}`;

export const RateCardsConfig = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [activeCombo, setActiveCombo] = useState<ComboKey | null>(null);
  const [brackets, setBrackets] = useState<Bracket[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data: entityTypes } = useQuery({
    queryKey: ["entity_types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("entity_types")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return data as EntityType[];
    },
  });

  const { data: crops } = useQuery({
    queryKey: ["crops"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crops")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as Crop[];
    },
  });

  const { data: allBrackets } = useQuery({
    queryKey: ["pricing_brackets"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pricing_brackets")
        .select("*")
        .order("min_value");
      if (error) throw error;
      return data as Bracket[];
    },
  });

  // Only show combos that have existing pricing brackets
  const combos = useMemo(() => {
    if (!entityTypes || !crops || !allBrackets) return [];
    const existingCombos = new Set(
      allBrackets.map((b) => comboKey(b.entity_type_id, b.crop_id)),
    );
    const result: {
      key: ComboKey;
      label: string;
      unit: string;
      entityTypeId: number;
      cropId: number;
    }[] = [];
    for (const et of entityTypes) {
      for (const crop of crops) {
        const key = comboKey(et.id, crop.id);
        if (existingCombos.has(key)) {
          result.push({
            key,
            label: `${crop.name} ${et.name}`,
            unit: et.unit,
            entityTypeId: et.id,
            cropId: crop.id,
          });
        }
      }
    }
    return result;
  }, [entityTypes, crops, allBrackets]);

  // All possible combos for the "Add" dropdown
  const availableCombos = useMemo(() => {
    if (!entityTypes || !crops) return [];
    const existingKeys = new Set(combos.map((c) => c.key));
    const result: {
      key: ComboKey;
      label: string;
      entityTypeId: number;
      cropId: number;
    }[] = [];
    for (const et of entityTypes) {
      for (const crop of crops) {
        const key = comboKey(et.id, crop.id);
        if (!existingKeys.has(key)) {
          result.push({
            key,
            label: `${crop.name} ${et.name}`,
            entityTypeId: et.id,
            cropId: crop.id,
          });
        }
      }
    }
    return result;
  }, [entityTypes, crops, combos]);

  // Set first combo as active
  useEffect(() => {
    if (combos.length && !activeCombo) {
      setActiveCombo(combos[0].key);
    }
  }, [combos, activeCombo]);

  // Load brackets for active combo
  useEffect(() => {
    if (allBrackets && activeCombo) {
      const [etId, cropId] = activeCombo.split("-").map(Number);
      setBrackets(
        allBrackets.filter(
          (b) => b.entity_type_id === etId && b.crop_id === cropId,
        ),
      );
      setDirty(false);
    }
  }, [allBrackets, activeCombo]);

  const activeInfo = combos.find((c) => c.key === activeCombo);
  const unitLabel = activeInfo?.unit?.toLowerCase() ?? "ha";

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!activeCombo) return;
      const [etId, cropId] = activeCombo.split("-").map(Number);

      // Delete existing brackets for this combo
      await supabase
        .from("pricing_brackets")
        .delete()
        .eq("entity_type_id", etId)
        .eq("crop_id", cropId);

      if (brackets.length > 0) {
        const { error } = await supabase
          .from("pricing_brackets")
          .insert(
            brackets.map((b) => ({
              entity_type_id: etId,
              crop_id: cropId,
              min_value: b.min_value,
              max_value: b.max_value,
              rate_per_unit: b.rate_per_unit,
            })),
          );
        if (error) throw error;
      }
    },
    onSuccess: () => {
      notify("Rate card saved", { type: "success" });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["pricing_brackets"] });
    },
    onError: () => notify("Failed to save", { type: "error" }),
  });

  const handleChange = (
    index: number,
    field: keyof Bracket,
    value: string,
  ) => {
    const num = value === "" ? 0 : Number(value);
    if (Number.isNaN(num)) return;
    setBrackets((prev) =>
      prev.map((b, i) =>
        i === index
          ? {
              ...b,
              [field]:
                field === "max_value" && value === "" ? null : num,
            }
          : b,
      ),
    );
    setDirty(true);
  };

  const handleAdd = () => {
    if (!activeCombo) return;
    const [etId, cropId] = activeCombo.split("-").map(Number);
    const lastBracket = brackets[brackets.length - 1];
    const newMin = lastBracket
      ? (lastBracket.max_value ?? lastBracket.min_value + 1000)
      : 0;
    setBrackets((prev) => [
      ...prev,
      {
        id: -Date.now(),
        entity_type_id: etId,
        crop_id: cropId,
        min_value: newMin,
        max_value: null,
        rate_per_unit: 0,
      },
    ]);
    setDirty(true);
  };

  const handleRemove = (index: number) => {
    setBrackets((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Set pricing per entity type + crop combination. Deal values are
        calculated as: total size x rate x 12.
      </p>

      {/* Combo tabs + add new combo */}
      <div className="flex gap-1 border-b flex-wrap items-center">
        {combos.map((combo) => (
          <button
            key={combo.key}
            type="button"
            onClick={() => setActiveCombo(combo.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeCombo === combo.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {combo.label}
          </button>
        ))}
        {availableCombos.length > 0 && (
          <select
            className="h-8 rounded-md border bg-background px-2 text-xs text-muted-foreground ml-1"
            value=""
            onChange={(e) => {
              const key = e.target.value as ComboKey;
              if (!key) return;
              const [etId, cropId] = key.split("-").map(Number);
              // Create an empty bracket to make the combo appear
              setBrackets([
                {
                  id: -Date.now(),
                  entity_type_id: etId,
                  crop_id: cropId,
                  min_value: 0,
                  max_value: null,
                  rate_per_unit: 0,
                },
              ]);
              setActiveCombo(key);
              setDirty(true);
            }}
          >
            <option value="">+ Add combo...</option>
            {availableCombos.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Brackets table */}
      {activeCombo && (
        <div>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left py-2 text-xs font-medium text-muted-foreground">
                  From ({activeInfo?.unit})
                </th>
                <th className="text-left py-2 text-xs font-medium text-muted-foreground">
                  To ({activeInfo?.unit})
                </th>
                <th className="text-left py-2 text-xs font-medium text-muted-foreground">
                  Rate (RM/{unitLabel}/month)
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {brackets.map((b, i) => (
                <tr key={b.id}>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      value={b.min_value}
                      onChange={(e) =>
                        handleChange(i, "min_value", e.target.value)
                      }
                      className="h-8 w-28 rounded-md border bg-background px-2 text-sm"
                      min={0}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      value={b.max_value ?? ""}
                      onChange={(e) =>
                        handleChange(i, "max_value", e.target.value)
                      }
                      placeholder="No limit"
                      className="h-8 w-28 rounded-md border bg-background px-2 text-sm"
                      min={0}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="number"
                      value={b.rate_per_unit}
                      onChange={(e) =>
                        handleChange(i, "rate_per_unit", e.target.value)
                      }
                      className="h-8 w-24 rounded-md border bg-background px-2 text-sm"
                      min={0}
                      step={0.1}
                    />
                  </td>
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(i)}
                      className="text-destructive hover:text-destructive h-8 w-8 p-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-1" />
              Add Bracket
            </Button>
            {dirty && (
              <Button
                size="sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
